// 🔧 social_defaults.js
// ✅ Purpose: Provides draft social content for Demo Engine events
// ✅ Features:
// - Creates promotion drafts for before, during, and after event moments
// - Adapts to Sponsors, Vendors, Auction, Store, Tickets, and Passport
// - Keeps posts as drafts only
// - Returns plain JavaScript objects only
// - Fully block-commented for clarity and future devs

import { getIndustryTemplate } from "../../../industries";
import { getPowerUpProfile } from "../../../industries/power_up_profiles";

export const buildSocialDefaults = ({
  industryTemplate = "car_show",
  demoSize = "medium",
  demoContext,
} = {}) => {
  const industry = getIndustryTemplate(industryTemplate);
  const entryLabel = String(industry?.entryLabel || "entry").toLowerCase();
  const voterLabel = String(industry?.voterLabel || "voter").toLowerCase();
  const eventName =
    demoContext?.event?.name ||
    demoContext?.eventName ||
    "Demo Event";

  const eventSlug =
    demoContext?.event?.slug ||
    demoContext?.eventslug ||
    demoContext?.eventSlug ||
    "demo-event";

  const baseUrl = `/${eventSlug}/event`;

  const posts = [
    buildPost({
      title: "Event Announcement",
      timing: "before_event",
      platform: "facebook",
      campaign: "event_announcement",
      caption: `We're excited to announce ${eventName}! Explore the ${entryLabel} showcase, participate as a ${voterLabel}, and enjoy a full event experience powered by Let's Go Vote.`,
      linkPath: baseUrl,
      hashtags: buildHashtags({ industryTemplate }),
      displayOrder: 10,
    }),

    buildPost({
      title: "Voting Reminder",
      timing: "during_event",
      platform: "facebook",
      campaign: "voting_reminder",
      caption: `Voting is open at ${eventName}! Explore the ${entryLabel} choices, scan QR codes, and help choose the winners.`,
      linkPath: baseUrl,
      hashtags: buildHashtags({ industryTemplate, extra: ["Voting", "EventDay"] }),
      displayOrder: 20,
    }),

    buildPost({
      title: "Schedule Reminder",
      timing: "during_event",
      platform: "instagram",
      campaign: "schedule_reminder",
      caption: `Don't miss the highlights at ${eventName}. Check the event schedule and plan your day.`,
      linkPath: `/${eventSlug}/event`,
      hashtags: buildHashtags({ industryTemplate, extra: ["EventSchedule"] }),
      displayOrder: 30,
    }),
  ];

  posts.push(...buildSponsorPosts({ demoContext, eventSlug }));
  posts.push(...buildVendorPosts({ demoContext, eventSlug }));
  posts.push(...buildAuctionPosts({ demoContext, eventSlug }));
  posts.push(...buildStorePosts({ demoContext, eventSlug }));
  posts.push(...buildTicketPosts({ demoContext, eventSlug }));
  posts.push(...buildPassportPosts({ demoContext, eventSlug }));

  posts.push(
    buildPost({
      title: "Thank You Recap",
      timing: "after_event",
      platform: "facebook",
      campaign: "event_recap",
      caption: `Thank you to every ${voterLabel}, participant, volunteer, sponsor, and vendor who helped make ${eventName} a success!`,
      linkPath: baseUrl,
      hashtags: buildHashtags({ industryTemplate, extra: ["ThankYou"] }),
      displayOrder: 900,
    })
  );

  return {
    posts: posts.slice(0, getPostLimit(demoSize)),
  };
};

const buildSponsorPosts = ({ demoContext, eventSlug }) => {
  const sponsors = demoContext?.sponsors?.sponsors || [];

  return sponsors.slice(0, 3).map((sponsor, index) =>
    buildPost({
      title: `Sponsor Spotlight: ${sponsor.name || "Event Sponsor"}`,
      timing: "before_event",
      platform: "facebook",
      campaign: "sponsor_spotlight",
      caption: `Sponsor spotlight: Thank you to ${sponsor.name || "our event sponsor"} for supporting this event and helping create a better attendee experience.`,
      linkPath: `/${eventSlug}/sponsors`,
      hashtags: ["SponsorSpotlight", "CommunitySupport", "LetsGoVote"],
      displayOrder: 100 + index,
      relatedModule: "sponsors",
      relatedId: sponsor.id || "",
      relatedName: sponsor.name || "",
    })
  );
};

const buildVendorPosts = ({ demoContext, eventSlug }) => {
  const vendors = demoContext?.vendors?.vendors || [];

  return vendors.slice(0, 3).map((vendor, index) =>
    buildPost({
      title: `Vendor Feature: ${vendor.name || "Event Vendor"}`,
      timing: "during_event",
      platform: "instagram",
      campaign: "vendor_feature",
      caption: `Vendor feature: Visit ${vendor.name || "one of our event vendors"} during the event and check out what they have to offer.`,
      linkPath: `/${eventSlug}/vendors`,
      hashtags: ["VendorFeature", "ShopLocal", "EventDay", "LetsGoVote"],
      displayOrder: 200 + index,
      relatedModule: "vendors",
      relatedId: vendor.id || "",
      relatedName: vendor.name || "",
    })
  );
};

const buildAuctionPosts = ({ demoContext, eventSlug }) => {
  const items = demoContext?.auction?.items || [];

  if (items.length === 0) return [];

  return [
    buildPost({
      title: "Silent Auction Reminder",
      timing: "during_event",
      platform: "facebook",
      campaign: "auction_reminder",
      caption: "The silent auction is live! Browse items, place bids, and support the event.",
      linkPath: `/${eventSlug}/auction`,
      hashtags: ["SilentAuction", "BidNow", "EventFundraiser", "LetsGoVote"],
      displayOrder: 300,
      relatedModule: "auction",
    }),
  ];
};

const buildStorePosts = ({ demoContext, eventSlug }) => {
  const products = demoContext?.store?.products || [];

  if (products.length === 0) return [];

  return [
    buildPost({
      title: "Merch Store Spotlight",
      timing: "during_event",
      platform: "instagram",
      campaign: "store_spotlight",
      caption: "Event merch is available! Browse shirts, stickers, collectibles, and more while supplies last.",
      linkPath: `/${eventSlug}/store`,
      hashtags: ["EventMerch", "MerchStore", "LetsGoVote"],
      displayOrder: 400,
      relatedModule: "store",
    }),
  ];
};

const buildTicketPosts = ({ demoContext, eventSlug }) => {
  const tickets = demoContext?.tickets?.tickets || [];

  if (tickets.length === 0) return [];

  return [
    buildPost({
      title: "Ticket Reminder",
      timing: "before_event",
      platform: "facebook",
      campaign: "ticket_reminder",
      caption: "Planning to attend? Grab your ticket and get ready for a great event experience.",
      linkPath: `/${eventSlug}/tickets`,
      hashtags: ["Tickets", "EventAccess", "LetsGoVote"],
      displayOrder: 500,
      relatedModule: "tickets",
    }),
  ];
};

const buildPassportPosts = ({ demoContext, eventSlug }) => {
  const stops = demoContext?.passport?.stops || [];

  if (stops.length === 0) return [];

  return [
    buildPost({
      title: "Passport Challenge",
      timing: "during_event",
      platform: "instagram",
      campaign: "passport_challenge",
      caption: "Try the event Passport! Scan stops, earn points, unlock achievements, and explore more of the event.",
      linkPath: `/${eventSlug}/passport`,
      hashtags: ["EventPassport", "Engagement", "Achievements", "LetsGoVote"],
      displayOrder: 600,
      relatedModule: "passport",
    }),
  ];
};

const buildPost = ({
  title,
  timing,
  platform,
  campaign,
  caption,
  linkPath,
  hashtags,
  displayOrder,
  relatedModule = "",
  relatedId = "",
  relatedName = "",
}) => ({
  title,
  timing,
  platform,
  campaign,
  caption,
  linkPath,
  hashtags,
  relatedModule,
  relatedId,
  relatedName,

  status: "draft",
  published: false,
  scheduledAt: null,
  publishedAt: null,

  displayOrder,
  demoSeed: true,
});

const buildHashtags = ({
  industryTemplate,
  extra = [],
} = {}) => {
  const base =
    getPowerUpProfile(industryTemplate).hashtags;

  return [...base, ...extra, "LetsGoVote"];
};

const getPostLimit = (demoSize) => {
  switch (demoSize) {
    case "small":
      return 8;

    case "large":
      return 24;

    case "xlarge":
      return 36;

    case "medium":
    default:
      return 16;
  }
};
